import { createContext, useContext, useRef } from 'react'
import { create, StoreApi, useStore } from 'zustand'

import { ToastDetails } from './toastTypings'

export interface ToastState {
  queue: ToastDetails[]
  /**
   * Queues a toast. Accepts an optional caller-supplied stable `id`; when omitted an id is generated.
   * Returns the id of the toast that is (or already was) queued, so callers can dismiss it via
   * `removeFromQueue`. Early-returns (refresh singleton, unduplicated duplicate) return the id of the
   * toast that satisfies the short-circuit, so the returned id is always usable for dismissal.
   */
  pushToQueue: (notification: Omit<ToastDetails, 'id'> & { id?: string }) => string
  removeFromQueue: (id: string) => void
}

export const createToastStore = () =>
  create<ToastState>((set, get) => ({
    queue: [],
    pushToQueue: (notification) => {
      const id = notification.id ?? Date.now().toString()
      const existingToastWithId = notification.id
        ? get().queue.find((toast) => toast.id === notification.id)
        : undefined

      if (existingToastWithId) return existingToastWithId.id

      if (notification.type === 'refresh') {
        const existingToast = get().queue.find((toast) => toast.type === 'refresh')
        if (existingToast) return existingToast.id
      }

      const duplicatedToast = get().queue.find(
        (toast) => toast.title === notification.title && toast.description === notification.description
      )

      if (duplicatedToast && !duplicatedToast.disableDuplication) return duplicatedToast.id

      set(({ queue }) => ({
        queue: [...queue, { id, ...notification }],
      }))
      return id
    },
    removeFromQueue: (id) => {
      set(({ queue }) => ({
        queue: queue.filter((notification) => notification.id !== id),
      }))
    },
  }))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ToastStoreContext = createContext<StoreApi<ToastState>>(undefined as any)
export const ToastStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const store = useRef<StoreApi<ToastState>>()

  if (!store.current) {
    store.current = createToastStore()
  }

  return <ToastStoreContext.Provider value={store.current}>{children}</ToastStoreContext.Provider>
}

export function useToastStore<T>(selector: (state: ToastState) => T) {
  const store = useContext(ToastStoreContext)

  return useStore(store, selector)
}
